import {
	Avatar,
	Box,
	BoxProps,
	IconButton,
	Stack,
	Typography
} from "@mui/material";
import React, {FC, useCallback, useState} from "react";
import {makeStyles} from "tss-react/mui";
import CloseIcon from "@mui/icons-material/Close";
import {IAnnouncement} from "@/types/announcement";
import {AnnouncementModal} from "@/pages/HomePage/Components/AnnouncementModal";
import {IUserEntity} from "@buildwitt/shapeshift-types/entities";
import {formatRelativeDate} from "@/utils/date-formatters";
import {getAnnouncementAuthorDisplayDate} from "@/app2/pages/ManagerView/Pages/Announcements/announcementAdminStatus";


interface IProps extends BoxProps {
	announcement: IAnnouncement;
	onDismiss?: (id: number) => void;
	isDismissing?: boolean;
}

const useStyles = makeStyles()((theme) => ({
	itemContent: {
		width: 300,
		height: 207,
		background: "rgba(255, 255, 255, 0.12)",
		border: `1px solid ${theme.palette.primary.main}`,
		flex: "0 0 auto",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		boxShadow: theme.shadows[2],
		margin: 4,
		position: "relative",
		cursor: "pointer",
		transition: "all 0.3s ease",
		borderRadius: 24,
		opacity: 1,
		transform: "scale(1)",
		"&:hover": {
			transform: "translateY(-4px) scale(1)",
			boxShadow: theme.shadows[4]
		}
	},
	dismissing: {
		opacity: 0,
		transform: "scale(0.8) !important",
		pointerEvents: "none",
		transition: "opacity 0.3s ease, transform 0.3s ease",
		"&:hover": {
			transform: "scale(0.8) !important"
		}
	},
	cardContents: {
		display: "flex",
		flexDirection: "column",
		height: "100%",
		padding: 16,
		boxSizing: "border-box"
	},
	title: {
		color: theme.palette.text.primary,
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		overflow: "hidden",
		fontWeight: 700
	},
	content: {
		color: "rgba(255,255,255,0.8)",
		display: "-webkit-box",
		WebkitLineClamp: 2,
		WebkitBoxOrient: "vertical",
		overflow: "hidden"
	},
	seeMore: {
		color: theme.palette.grey[400],
		cursor: "pointer",
		"&:hover": {
			textDecoration: "underline",
			color: theme.palette.primary.main
		}
	},
	authorAvatar: {
		width: 56,
		height: 56
	}
}));

export const AnnouncementAuthorComponent: FC<{
	author: IUserEntity;
	date: Date;
}> = ({author, date}) => {
	const {classes} = useStyles();

	return (
		<Stack
			direction="row"
			spacing={1}
			alignItems="center"
			marginBottom={1}
			aria-label="Announcement author information"
			data-test-id="announcement-author"
		>
			<Avatar
				src={author?.profileImage?.toString()}
				className={classes.authorAvatar}
				aria-label="Author avatar"
				data-test-id="announcement-author-avatar"
			>
				{author?.lastname?.charAt(0) || "?"}
			</Avatar>

			<Stack direction="column" alignItems="flex-start">
				<Typography
					variant="body2"
					className={classes.title}
					sx={{WebkitLineClamp: 1}}
					data-test-id="announcement-author-name"
				>
					{`${author?.firstname ?? ""} ${author?.lastname ?? ""}`}
				</Typography>

				<Typography
					variant="body2"
					className={classes.content}
					data-test-id="announcement-author-title"
				>
					{author?.jobTitle ?? ""}
				</Typography>

				<Typography
					variant="body2"
					className={classes.content}
					data-test-id="announcement-published-date"
				>
					{formatRelativeDate(date)}
				</Typography>
			</Stack>
		</Stack>
	);
};

export const AnnouncementCard: FC<IProps> = ({className, announcement, onDismiss, isDismissing = false}) => {
	const {classes, cx} = useStyles();
	const [openModal, setOpenModal] = useState(false);

	const handleDismiss = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		onDismiss?.(announcement.id);
	}, [announcement.id, onDismiss]);

	return (
		<>
			<Box
				className={cx(classes.itemContent, className, isDismissing && classes.dismissing)}
				role="article"
				tabIndex={0}
				aria-label={`Announcement card: ${announcement.title}`}
				data-test-id={`announcement-card-${announcement.id}`}
				onClick={() => !isDismissing && setOpenModal(true)}
			>
				<Box className={classes.cardContents}>
					<Box display="flex" justifyContent="space-between" alignItems={"flex-start"}>
						<AnnouncementAuthorComponent
							author={announcement.author}
							date={getAnnouncementAuthorDisplayDate({
								publishedAt: announcement.publishedAt,
								scheduledDeliveryDate: announcement.scheduledDeliveryDate
							})}
						/>

						<IconButton
							color="primary"
							aria-label="Dismiss announcement"
							data-test-id="announcement-dismiss-btn"
							onClick={handleDismiss}
						>
							<CloseIcon />
						</IconButton>
					</Box>

					<Typography
						variant="body1"
						sx={{WebkitLineClamp: announcement.subtitle ? 1 : 2}}
						className={classes.title}
						data-test-id="announcement-title"
					>
						{announcement.title}
					</Typography>

					<Typography
						variant="body2"
						sx={{WebkitLineClamp:  2, marginTop: "4px"}}
						className={classes.content}
						data-test-id="announcement-title"
					>
						{announcement.subtitle}
					</Typography>

					<Typography
						variant="caption"
						className={classes.seeMore}
						sx={{marginTop: "4px"}}
						aria-label="Open announcement details"
						data-test-id="announcement-see-more"
					>
						See more
					</Typography>
				</Box>
			</Box>

			{openModal && <AnnouncementModal
				open={openModal}
				onClose={() => setOpenModal(false)}
				announcementSlug={announcement.slug}
				author={announcement.author}
			/>}
		</>
	);
};
